<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import PlayoutService from '@/services/PlayoutService'
import StreamService from '@/services/StreamService'
import Badge from '../Badge.vue'
import FormButton from '../Atoms/FormButton.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import IconLinkChain from '../icon/IconLinkChain.vue'
import IconAlertTriangleStroke from '../icon/IconAlertTriangleStroke.vue'
import IconElipsisVertical from '../icon/IconElipsisVertical.vue'
import IconReplace from '../icon/IconReplace.vue'
import IconClock from '../icon/IconClock.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import StreamThumb from '../ui/StreamThumb.vue'
import StreamCardBadges from '../ui/StreamCard/StreamCardBadges.vue'
import ToggleStreamModal from '../modals/ToggleStreamModal.vue'
import ConfirmModal from '../modals/ConfirmModal.vue'

const props = defineProps({
	playout: {
		type: Object,
		default: () => ({}),
	},
})

const store = useStore()
const router = useRouter()
const { notify } = useNotification()

const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

const playoutConfig = ref(null)
const configLoaded = ref(false)
const loadPlayoutConfig = async () => {
	if (!props.playout?._id) return
	try {
		playoutConfig.value = await PlayoutService.getPlayoutConfig(props.playout._id)
		store.dispatch('Playout/cacheConfig', { id: props.playout._id, data: playoutConfig.value })
	} catch (e) {
		playoutConfig.value = null
	} finally {
		configLoaded.value = true
	}
}

onMounted(async () => {
	subscribeMediaPulse(props.playout)
	await loadPlayoutConfig()
})
onUnmounted(() => unsubscribeMediaPulse())

const open = () => {
	router.push({ name: 'PlayoutViewV2', params: { tvId: props.playout._id } })
}

const stop = (ev) => { ev?.preventDefault?.(); ev?.stopPropagation?.() }

/* enable / disable */
const isEnabled = computed(() => !!props.playout?.enabled)
const onToggleClick = (ev) => {
	stop(ev)
	showModal('modalToggleStream' + props.playout._id)
}

/* delete */
const hasDestination = computed(() => !!props.playout?.destination)
const deleteTooltip = computed(() => {
	if (!hasDestination.value) return ''
	const linked = props.playout.destination
	return `This TV Playout can't be deleted because it's used as a source in ${linked}. Remove it as a source first.`
})
const onDeleteClick = (ev) => {
	stop(ev)
	if (hasDestination.value) return
	showModal('delete-playout-v2-list-' + props.playout._id)
}
const onDeleteConfirm = async () => {
	hideModal('delete-playout-v2-list-' + props.playout._id)
	try {
		await StreamService.deleteStream(props.playout._id)
		await store.dispatch('Streams/getStreams', true)
		notify({ group: 'success', text: 'TV Playout deleted' })
	} catch (err) {
		notify({ group: 'error', text: err?.message || 'Failed to delete TV Playout' })
	}
}

/* meta */
const playoutFps = computed(() => playoutConfig.value?.settings?.fps || props.playout?.config?.fps || 25)
const playoutResolution = computed(() => {
	const raw = playoutConfig.value?.settings?.resolution || props.playout?.config?.resolution || '1280x720'
	if (raw === 'fullhd') return '1920x1080'
	if (raw === 'hd') return '1280x720'
	return raw
})
const resolutionLabel = computed(() => {
	const map = {
		'3840x2160': '4K',
		'2560x1440': '1440p',
		'1920x1080': '1080p',
		'1280x720': '720p',
		'854x480': '480p',
		'640x360': '360p',
	}
	return map[playoutResolution.value] || playoutResolution.value
})
const filesCount = computed(() => playoutConfig.value?.playlist?.length ?? 0)
const isEmptyPlaylist = computed(() => configLoaded.value && filesCount.value === 0)
const totalDurationMs = computed(() => {
	const items = playoutConfig.value?.playlist || []
	return items.reduce((sum, it) => {
		const span = (it?.endTime || 0) - (it?.startTime || 0)
		return sum + (span > 0 ? span : 0)
	}, 0)
})
const totalDurationLabel = computed(() => {
	const ms = totalDurationMs.value
	if (!ms) return ''
	const totalMin = Math.round(ms / 60000)
	if (totalMin < 1) return '<1m'
	const days = Math.floor(totalMin / 1440)
	const hours = Math.floor((totalMin % 1440) / 60)
	const mins = totalMin % 60
	if (days > 0) return `${days}d ${hours}h`
	if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
	return `${mins}m`
})
const playoutMode = computed(() => {
	const mode = playoutConfig.value?.mode || props.playout?.config?.mode
	return mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : ''
})
const detailsLine = computed(() => {
	const parts = [resolutionLabel.value, `${playoutFps.value} fps`]
	if (filesCount.value > 0) {
		parts.push(`${filesCount.value} ${filesCount.value === 1 ? 'clip' : 'clips'}`)
		if (totalDurationLabel.value) parts.push(totalDurationLabel.value)
	}
	return parts.join(' • ')
})
const destinationName = computed(() => props.playout?.destination || '')
const hasDestinationName = computed(() => !!destinationName.value)
const playoutModeIcon = computed(() => {
	const mode = playoutConfig.value?.mode || props.playout?.config?.mode
	switch (mode) {
		case 'loop': return IconReplace
		case 'schedule': return IconClock
		default: return IconReplace
	}
})
</script>

<template>
	<div class="playout-list-item w-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2">
		<!-- Thumbnail / live preview -->
		<div class="playout-list-item__thumb relative shrink-0 overflow-hidden rounded h-12 w-24 cursor-pointer bg-gradient-to-b from-surface-darker to-black"
			@click="open">
			<StreamThumb class="playout-thumb w-full h-full" :stream="playout" :media-pulse="mediaPulse" :compact-mode="true" />
		</div>

		<!-- Title, mode, destination -->
		<div class="playout-list-item__title min-w-0 max-w-full overflow-hidden cursor-pointer" @click="open">
			<div class="flex flex-col min-w-0 items-start">
				<div class="inline-flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
					<h3 class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0" :title="playout.name">{{ playout.name }}</h3>
					<Badge v-if="playoutMode" type="main" inline pill size="xs" class="shrink-0 gap-1">
						<icon-base width="12" height="12" :width-view-box="16" :height-view-box="16">
							<component :is="playoutModeIcon" />
						</icon-base>
						{{ playoutMode }}
					</Badge>
				</div>
				<p class="text-tiny truncate flex items-center gap-1.5 m-0 max-w-full"
					:class="hasDestinationName ? 'text-surface-8' : 'text-surface-6'"
					:title="hasDestinationName ? `This playout streams to “${destinationName}”.` : 'Link this playout to an output stream to broadcast it.'">
					<icon-base class="shrink-0" :width="12" :height="12" width-view-box="14" height-view-box="14" fill="none">
						<icon-link-chain />
					</icon-base>
					<span class="truncate">{{ hasDestinationName ? destinationName : 'No destination set' }}</span>
				</p>
			</div>
		</div>

		<div class="playout-list-item__kebab shrink-0 md:hidden" @click.stop>
			<DropDownMenu isIcon position="right" type="secondary" no-border>
				<template #toggle-button>
					<icon-base class="!ml-1">
						<IconElipsisVertical />
					</icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem @click="onToggleClick">
						{{ isEnabled ? 'Disable' : 'Enable' }}
					</DropDownMenuItem>
					<DropDownMenuItem :disabled="hasDestination" :title="hasDestination ? deleteTooltip : undefined" @click="onDeleteClick">
						Delete
					</DropDownMenuItem>
				</template>
			</DropDownMenu>
		</div>

		<p v-if="isEmptyPlaylist" class="playout-list-item__meta min-w-0 text-tiny text-gold truncate flex items-center gap-1.5 m-0 md:justify-end"
			title="This playout has no clips yet — add content so it has something to broadcast.">
			<icon-base class="shrink-0" :width="13" :height="13" width-view-box="24" height-view-box="24" fill="none">
				<icon-alert-triangle-stroke />
			</icon-base>
			<span class="truncate">No clips added yet</span>
		</p>
		<p v-else-if="configLoaded" class="playout-list-item__meta min-w-0 text-tiny text-surface-7 truncate m-0 md:text-right"
			:title="`${playoutResolution} • ${playoutFps} fps`">{{ detailsLine }}</p>

		<div class="playout-list-item__badges">
			<StreamCardBadges :stream="playout" :media-pulse="mediaPulse" :compact-mode="true" :hide-preview="false" />
		</div>

		<!-- Desktop: inline actions -->
		<div class="playout-list-item__actions hidden md:flex items-center gap-2 shrink-0" @click.stop>
			<Tooltip v-if="hasDestination" :content="deleteTooltip">
				<FormButton type="danger" size="sm" isIcon disabled>
					<icon-base><icon-bin /></icon-base>
				</FormButton>
			</Tooltip>
			<FormButton v-else type="danger" size="sm" isIcon @click="onDeleteClick">
				<icon-base><icon-bin /></icon-base>
			</FormButton>
			<FormButton :type="isEnabled ? 'secondary' : 'main'" size="sm" @click="onToggleClick">
				{{ isEnabled ? 'Disable' : 'Enable' }}
			</FormButton>
		</div>

		<ToggleStreamModal :stream="playout" />
		<ConfirmModal :modalId="'delete-playout-v2-list-' + playout._id"
			message="Are you sure you want to delete your TV Playout?" modal-type="danger"
			@modal-confirm="onDeleteConfirm" />
	</div>
</template>

<style scoped>
.playout-list-item {
	display: grid;
	align-items: center;
	column-gap: 0.75rem;
	row-gap: 0.25rem;
	grid-template-columns: auto minmax(0, 1fr) auto;
	grid-template-areas:
		"thumb title kebab"
		"badges badges badges"
		"meta meta meta";
}

.playout-list-item__thumb {
	grid-area: thumb;
}

.playout-list-item__title {
	grid-area: title;
}

.playout-list-item__kebab {
	grid-area: kebab;
	justify-self: end;
}

.playout-list-item__badges {
	grid-area: badges;
}

.playout-list-item__meta {
	grid-area: meta;
}

.playout-list-item__actions {
	grid-area: actions;
}

@media (min-width: 768px) {
	.playout-list-item {
		grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr) auto auto;
		grid-template-rows: auto;
		grid-template-areas: "thumb title meta badges actions";
	}
}

/* Make the shared (aspect-video) thumbnail fill the fixed-height area, cover-cropped */
.playout-thumb :deep(#thumb-container) {
	aspect-ratio: auto !important;
	height: 100% !important;
}
.playout-thumb :deep(#thumb-container) video,
.playout-thumb :deep(#thumb-container) img {
	height: 100% !important;
	width: 100% !important;
	object-fit: cover !important;
}
</style>
